import type { Button, TextLinkButton } from 'braid-design-system';
import { omit } from 'lodash';
import { createElement, useCallback } from 'react';

import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import type { Mode, Card, Page } from 'src/types';

export type LinkComponent =
  | React.ComponentType<{ href: string; children: React.ReactNode }>
  | string;

interface Props {
  card?: Card;
  page?: Page;
  mode?: Mode;
  params?: Record<string, unknown>;
  component?: LinkComponent;
  className?: string;
  analytics?: Record<string, unknown>;
}

type LinkProps = Props &
  React.ComponentProps<typeof Button> &
  React.ComponentProps<typeof TextLinkButton>;

export const Link = ({
  children,
  component = 'a',
  ...restProps
}: LinkProps) => {
  const navigate = useNavigateRoute();

  const onClickFn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const { card, page, mode, params = {}, onClick } = restProps;

      onClick?.(event);

      if (page) {
        navigate({
          page,
          params,
        });
      } else if (card) {
        navigate({
          card,
          mode,
          params,
        });
      }
    },
    [navigate, restProps],
  );

  const props = {
    ...omit(
      restProps,
      'card',
      'page',
      'mode',
      'params',
      'onClick',
      'analytics',
    ),
    onClick: onClickFn,
    children,
    href: '',
  };

  return createElement(component, props, children);
};
